import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { MODULES } from "@/config/modules";
import { useAuth } from "@/contexts/AuthContext";
import { printDocument } from "@/lib/printDoc";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { DevicesEditor } from "@/components/DevicesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Search, Download, Pencil, Trash2, Inbox, Lock, Eye, Printer, ArrowRight, X,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, Wallet, Check, Ban, Wrench, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { IT_SUPPORT_TYPE } from "@/lib/itSupport";
import { PaymentHistory } from "@/components/PaymentHistory";
import { Checkbox } from "@/components/ui/checkbox";

const statusBadge = (v) => {
  const s = String(v || "").toLowerCase();
  if (["paid", "full", "approved", "active", "completed", "received", "confirmed", "resolved", "qualified", "accepted", "in-use", "in-stock"].includes(s))
    return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20";
  if (["pending_approval", "pending_l1", "pending_l2", "pending", "unpaid_invoice", "partially_paid", "draft", "new", "open", "scheduled", "contacted", "in-progress", "partial", "sent"].includes(s))
    return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20";
  if (["overdue", "rejected", "cancelled", "lost", "expired", "critical", "disposed", "damaged"].includes(s))
    return "bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20";
  return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20";
};

// A module's permission key is its backend collection name, not its
// frontend endpoint path -- e.g. "/sales-orders" -> "sales_orders".
const collectionOf = (endpoint) => endpoint.replace(/^\//, "").replace(/-/g, "_");

const isMoneyKey = (k) => ["amount", "amount_paid", "price", "cost", "value", "balance", "credit_limit", "gross", "tax", "net", "salary", "unit_price", "total"].includes(k);
// Foreign-currency figures (USD invoices) keep their cents.
const isForeignMoneyKey = (k) => ["amount_fc", "tax_fc", "total_usd", "exchange_rate"].includes(k);
const fmtCell = (key, val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "object") return JSON.stringify(val);
  if (typeof val === "number" && isForeignMoneyKey(key)) {
    return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
  }
  if (typeof val === "number" && isMoneyKey(key)) return new Intl.NumberFormat("en-TZ").format(val);
  return String(val);
};
// Clearer names than the raw keys in the record detail view.
const DETAIL_LABELS = {
  amount_fc: "Subtotal (USD)", tax_fc: "VAT (USD)", exchange_rate: "Exchange rate (TZS per USD)",
};

export default function CrudPage({ moduleKey }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const key = moduleKey || params.moduleKey;
  const cfg = MODULES[key];

  const { user, can, isFieldHidden, permissions, canViewPage } = useAuth();
  const collection = cfg ? collectionOf(cfg.endpoint) : null;
  const canView = !cfg || can(collection, "view");
  const canCreate = !!cfg && can(collection, "create");
  const canEdit = !!cfg && can(collection, "edit");
  const canDelete = !!cfg && can(collection, "delete");
  const fieldVisible = (name) => !cfg || !isFieldHidden(collection, name);
  // A column is either a stored field or a derived value from cfg.computedColumns.
  const cellValue = (row, col) => (cfg?.computedColumns?.[col] ? cfg.computedColumns[col](row) : row[col]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [approvalTask, setApprovalTask] = useState(null); // { id, status, can_act } for the record being viewed
  const [approvalAction, setApprovalAction] = useState(null); // "approve" | "reject"
  const [approvalNote, setApprovalNote] = useState("");
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentHistorical, setPaymentHistorical] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [convertingProforma, setConvertingProforma] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [refOptions, setRefOptions] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Load reference options for any field with `ref`
  useEffect(() => {
    if (!cfg) return;
    const refs = cfg.fields.filter((f) => f.ref);
    if (refs.length === 0) { setRefOptions({}); return; }
    (async () => {
      const next = {};
      await Promise.all(refs.map(async (f) => {
        try {
          // High enough for every customer: a record must name one from this list.
          const { data } = await api.get(`${f.ref}/`, { params: { limit: 5000 } });
          next[f.name] = (data.items || []).map((it) => ({
            value: String(it[f.refValue || f.refLabel] || it[f.refLabel] || it.id),
            label: String(it[f.refLabel] || it.name || it.id),
          }));
        } catch {
          next[f.name] = [];
        }
      }));
      setRefOptions(next);
    })();
  }, [cfg]);

  const fetchData = useCallback(async () => {
    if (!cfg) return;
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { skip: (page - 1) * pageSize, limit: pageSize };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get(cfg.endpoint + "/", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [cfg, canView, q, statusFilter, dateFrom, dateTo, page, pageSize]);

  useEffect(() => {
    setQ(""); setStatusFilter("all"); setDateFrom(""); setDateTo("");
    setSortKey(null); setSortDir("asc"); setPage(1);
  }, [key]);

  // Any filter change (or page size change) should jump back to page 1 --
  // staying on e.g. page 5 after a search narrows the result set to 1 page
  // would just show an empty table.
  useEffect(() => { setPage(1); }, [q, statusFilter, dateFrom, dateTo, pageSize]);

  const toggleSort = (col) => {
    if (sortKey === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col);
      setSortDir("asc");
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = cellValue(a, sortKey), bv = cellValue(b, sortKey);
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      const an = Number(av), bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "") return (an - bn) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cellValue only depends on cfg
  }, [items, sortKey, sortDir, cfg]);

  useEffect(() => {
    const t = setTimeout(fetchData, 280);
    return () => clearTimeout(t);
  }, [fetchData]);

  // A read-only preview of the next sequential number (e.g. next WO No.) --
  // not reserved, just shown so the form never opens on a blank number
  // field. The real number is still assigned atomically when it's saved.
  const applyNumberPreview = () => {
    if (!cfg.previewNumberField) return;
    api.get(`${cfg.endpoint}/next-number`)
      .then(({ data }) => setForm((s) => ({ ...s, [cfg.previewNumberField]: data.value })))
      .catch(() => {});
  };

  const openCreate = () => { setEditing(null); setForm({}); setDialogOpen(true); applyNumberPreview(); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setDialogOpen(true); };
  // Some modules (e.g. Chart of Accounts) send you to a dedicated page
  // instead of the generic detail dialog when you open a record.
  const openView = (it) => {
    if (cfg.rowLink) { navigate(cfg.rowLink(it)); return; }
    setViewing(it);
    setApprovalTask(null);
    // Lets an approver act right from this record instead of hunting it
    // down on the separate Approvals page -- only fetched for modules that
    // actually go through the approval workflow, and only tells us whether
    // *this* viewer can act (the backend enforces the same rule either way).
    if (cfg.approval_required && String(it.approval_status || "").startsWith("pending")) {
      api.get("/approvals/by-record", { params: { collection, record_id: it.id } })
        .then(({ data }) => setApprovalTask(data.task))
        .catch(() => setApprovalTask(null));
    }
  };

  const submitApproval = async () => {
    if (!approvalTask || !approvalAction) return;
    setSubmittingApproval(true);
    try {
      await api.post(`/approvals/${approvalTask.id}/${approvalAction}`, { note: approvalNote });
      toast.success(approvalAction === "approve" ? "Approved" : "Rejected");
      setApprovalAction(null);
      setApprovalNote("");
      setViewing(null);
      setApprovalTask(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmittingApproval(false);
    }
  };

  // Arriving from the header's global search: fetch that one record directly
  // (it may not be on the current page/filter of the list) and open it,
  // same as clicking its row would.
  useEffect(() => {
    const openId = location.state?.openId;
    if (!openId || !cfg) return;
    (async () => {
      try {
        const { data } = await api.get(`${cfg.endpoint}/${openId}`);
        openView(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, cfg]);

  // Arriving from a "Create Work Order" button on a Ticket or Invoice's
  // detail view: open the New Work Order dialog pre-filled from that record.
  useEffect(() => {
    const prefill = location.state?.prefillWorkOrder;
    if (!prefill || !cfg || key !== "svc-work-orders") return;
    setEditing(null);
    setForm(prefill);
    setDialogOpen(true);
    applyNumberPreview();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, cfg, key]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      cfg.fields.forEach((f) => {
        if (f.readOnly && !editing) return; // auto-generated on create, never sent
        if (f.showIf && !f.showIf(form)) return; // conditionally hidden field
        let v = form[f.name];
        if (v === "" || v === undefined) return;
        if (f.type === "number") v = Number(v);
        payload[f.name] = v;
      });
      if (cfg.currency && form.invoice_type === IT_SUPPORT_TYPE) {
        // Every line prints "...deployed at <hospital> for the month of <MONTH>".
        const lines = form.items || [];
        if (!lines.length || lines.some((i) => !i.hospital || !i.service_month)) {
          toast.error("Choose a hospital and a month on every line");
          setSaving(false);
          return;
        }
      }
      if (cfg.lineItems) {
        payload.amount = form.amount || 0;
        payload.tax = form.tax || 0;
        if (cfg.currency) {
          payload.currency = form.currency || "TZS";
          if (payload.currency === "USD") {
            if (!(Number(form.exchange_rate) > 0)) {
              toast.error("Enter the exchange rate (TZS per 1 USD) for a USD invoice");
              setSaving(false);
              return;
            }
            payload.exchange_rate = Number(form.exchange_rate);
            payload.amount_fc = form.amount_fc || 0;
            payload.tax_fc = form.tax_fc || 0;
          }
        }
        payload.vat_enabled = !!form.vat_enabled;
        payload.items = form.items || [];
        payload.linked_invoice = form.linked_invoice || "";
        if (form.next_due_date) payload.next_due_date = form.next_due_date;
      }
      if (editing) {
        await api.put(`${cfg.endpoint}/${editing.id}`, payload);
        toast.success(`${cfg.title.replace(/s$/, "")} updated`);
      } else {
        await api.post(cfg.endpoint + "/", payload);
        toast.success(cfg.approval_required
          ? `Created and sent for approval`
          : `${cfg.title.replace(/s$/, "")} created`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`${cfg.endpoint}/${confirmDelete.id}`);
      toast.success("Deleted");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDelete(null);
    }
  };

  const exportCsv = async () => {
    const coll = cfg.endpoint.replace(/^\//, "").replace(/-/g, "_");
    const url = `${API_BASE}/reports/export/${coll}`;
    const token = localStorage.getItem("kn_token");
    try {
      const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Export not available");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${coll}.csv`;
      a.click();
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export not available for this module");
    }
  };

  const doConvert = async (target) => {
    if (!viewing) return;
    try {
      const payload = { ...viewing };
      delete payload.id; delete payload.status; delete payload.created_at;
      delete payload.updated_at; delete payload.created_by; delete payload.updated_by;
      delete payload.approval_status;
      // Generate new doc number prefix
      const prefixMap = { "/invoices/": "INV", "/sales-orders/": "SO" };
      const prefix = prefixMap[target.endpoint] || "DOC";
      const docNo = `${prefix}-CONV-${Date.now().toString().slice(-6)}`;
      if (target.endpoint.includes("invoices")) payload.invoice_no = docNo;
      else if (target.endpoint.includes("sales-orders")) payload.order_no = docNo;
      payload.status = "draft";
      payload.source_doc = viewing.id;
      await api.post(target.endpoint, payload);
      toast.success(`Converted to ${target.to}`);
      setViewing(null);
      navigate(target.path);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const outstandingBalance = (inv) =>
    ((inv?.amount || 0) + (inv?.tax || 0)) - (inv?.amount_paid || 0);

  const openPayment = (inv) => {
    setPayingInvoice(inv);
    setPaymentAmount(String(outstandingBalance(inv)));
    setPaymentMethod("");
    setTransactionId("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentHistorical(false);
    if (paymentAccounts.length === 0) {
      api.get("/chart-of-accounts/", { params: { limit: 100 } })
        .then(({ data }) => setPaymentAccounts(data.items || []))
        .catch(() => {});
    }
  };

  const submitPayment = async () => {
    if (!payingInvoice) return;
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) { toast.error("Enter an amount greater than zero"); return; }
    if (!paymentMethod) { toast.error("Select a payment method"); return; }
    if (!paymentDate) { toast.error("Enter the date the payment was received"); return; }
    setRecordingPayment(true);
    try {
      await api.post(`/invoices/${payingInvoice.id}/record-payment`, {
        amount, payment_method: paymentMethod, transaction_id: transactionId, payment_date: paymentDate,
        historical: paymentHistorical,
      });
      toast.success("Payment recorded and added to Sales");
      setPayingInvoice(null);
      setViewing(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecordingPayment(false);
    }
  };

  const convertProformaToTax = async () => {
    if (!viewing) return;
    setConvertingProforma(true);
    try {
      // In-place conversion -- same document, no duplicate record. Backend
      // assigns a fresh real Tax Invoice number (never keeps the PRO-00001
      // style number, since that sequence has to stay a clean legal record).
      const { data } = await api.post(`/invoices/${viewing.id}/convert-to-tax`);
      toast.success(`Converted to Tax Invoice ${data.invoice_no}`);
      setViewing(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConvertingProforma(false);
    }
  };

  const doPrint = () => {
    if (!viewing) return;
    if (cfg.customPrint) { cfg.customPrint(viewing); return; }
    printDocument({
      title: cfg.title.replace(/s$/, ""),
      subtitle: cfg.subtitle,
      record: viewing,
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fieldVisible closes over `permissions`, already a dep
  const cols = useMemo(() => (cfg?.columns || []).filter(fieldVisible), [cfg, permissions]);
  const statusOptions = cfg?.statusOptions || [];

  if (!cfg) return <div className="text-muted-foreground">Module not found</div>;

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="font-display text-lg font-medium">You don't have permission to view this module</p>
        <p className="text-sm text-muted-foreground mt-1">Ask an administrator to grant you access from Roles &amp; Permissions.</p>
      </div>
    );
  }

  const renderField = (f) => {
    if (f.readOnly) {
      // Shows form[f.name] whenever there is one -- either the existing
      // value on edit, or a previewed number already filled in on create
      // (see applyNumberPreview) -- and falls back to the placeholder only
      // when nothing has been filled in yet.
      return (
        <Input
          data-testid={`field-${f.name}`}
          value={form[f.name] ?? ""}
          placeholder="Auto-generated on save"
          disabled
        />
      );
    }
    const opts = f.ref ? (refOptions[f.name] || []) : (f.options ? f.options.map((o) => ({ value: o, label: o })) : null);
    const v = form[f.name];
    if (opts && f.searchable) {
      return (
        <SearchableSelect
          value={v !== undefined && v !== null ? String(v) : ""}
          onValueChange={(val) => setForm((s) => ({ ...s, [f.name]: val }))}
          options={opts}
          placeholder={`Select ${f.label}`}
          testId={`field-${f.name}`}
        />
      );
    }
    if (opts) {
      return (
        <Select value={v !== undefined && v !== null ? String(v) : ""} onValueChange={(val) => setForm((s) => ({ ...s, [f.name]: val }))}>
          <SelectTrigger data-testid={`field-${f.name}`}>
            <SelectValue placeholder={`Select ${f.label}`} />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") {
      return (
        <Textarea
          data-testid={`field-${f.name}`}
          value={v ?? ""}
          placeholder={f.placeholder}
          rows={3}
          onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
        />
      );
    }
    if (f.type === "number" && isMoneyKey(f.name)) {
      return (
        <MoneyInput
          data-testid={`field-${f.name}`}
          value={v ?? ""}
          placeholder={f.placeholder}
          allowNegative={f.name === "balance"}
          onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
        />
      );
    }
    return (
      <Input
        data-testid={`field-${f.name}`}
        type={f.type || "text"}
        value={v ?? ""}
        placeholder={f.placeholder}
        onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
      />
    );
  };

  return (
    <div className="space-y-6" data-testid={`crud-page-${key}`}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">{cfg.title}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{cfg.title}</h1>
          <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
            <span>{cfg.subtitle}</span>
            {cfg.approval_required && (
              <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-600 dark:text-amber-400">
                Approval required
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {canCreate ? (
            <Button onClick={openCreate} data-testid="create-item-button" className="kn-gradient-bg text-white border-0 hover:opacity-95">
              <Plus className="w-4 h-4 mr-2" /> New
            </Button>
          ) : (
            <Button disabled variant="outline" title="You don't have permission to add records here">
              <Lock className="w-3.5 h-3.5 mr-2" /> Read-only
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <Card className="p-4 rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="search-input"
              placeholder={`Search ${cfg.title.toLowerCase()}…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-muted/30"
            />
          </div>
          {statusOptions.length > 0 && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="filter-date-from" placeholder="From" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="filter-date-to" placeholder="To" />
          {(q || statusFilter !== "all" || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatusFilter("all"); setDateFrom(""); setDateTo(""); }}>
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-muted-foreground">Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-20" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[25, 50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="text-xs">
              {total === 0 ? "0 records" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
              <Inbox className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-display text-base font-medium">No {cfg.title.toLowerCase()} yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              {canCreate ? "Click \"New\" to create the first record." : "Records will appear here once added."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  {cols.map((c) => {
                    const active = sortKey === c;
                    const SortIcon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                    return (
                      <TableHead key={c} className="text-xs uppercase tracking-wider font-semibold p-0">
                        <button
                          type="button"
                          onClick={() => toggleSort(c)}
                          data-testid={`sort-${c}`}
                          className={`flex items-center gap-1.5 w-full h-full px-4 py-3 text-left transition-colors hover:text-foreground ${active ? "text-foreground" : ""}`}
                        >
                          {c.replace(/_/g, " ")}
                          <SortIcon className={`w-3 h-3 shrink-0 ${active ? "opacity-100" : "opacity-30"}`} />
                        </button>
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-28 text-right text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.map((it) => (
                  <TableRow key={it.id} data-testid={`row-${it.id}`} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => openView(it)}>
                    {cols.map((c) => (
                      <TableCell key={c} className="text-sm">
                        {(c === "status" || c === "priority") ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${statusBadge(it[c])}`}>
                            {fmtCell(c, it[c])}
                          </span>
                        ) : (
                          fmtCell(c, cellValue(it, c))
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openView(it)} data-testid={`view-${it.id}`} title="View">
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {canEdit && (
                          <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(it)} data-testid={`delete-${it.id}`} className="hover:text-destructive" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {!loading && total > pageSize && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Page {page} of {Math.ceil(total / pageSize)}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" data-testid="prev-page-button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
            </Button>
            <Button
              variant="outline" size="sm" data-testid="next-page-button"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Edit ${cfg.title.replace(/s$/, "")}` : `New ${cfg.title.replace(/s$/, "")}`}
            </DialogTitle>
            {cfg.approval_required && !editing && (
              <DialogDescription>This record will be marked <strong>pending approval</strong> and routed to Manager → SuperAdmin.</DialogDescription>
            )}
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto">
            {cfg.fields.filter((f) => (!f.showIf || f.showIf(form)) && fieldVisible(f.name)).map((f) => (
              <div key={f.name} className={`space-y-1.5 ${f.type === "textarea" ? "md:col-span-2" : ""}`}>
                <Label className="text-xs uppercase tracking-[0.16em]">
                  {f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}
                </Label>
                {renderField(f)}
              </div>
            ))}
            {cfg.lineItems && dialogOpen && (
              <LineItemsEditor
                key={editing?.id || "new"}
                value={form}
                allowInvoiceLink={!!cfg.linkableInvoice}
                allowVat={!!cfg.vatToggle}
                allowCurrency={!!cfg.currency}
                onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
              />
            )}
            {cfg.devices && dialogOpen && (
              <DevicesEditor
                key={editing?.id || "new"}
                value={form}
                onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-item-button" className="kn-gradient-bg text-white border-0">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {cfg.title.replace(/s$/, "")} Details
              {viewing?.status && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${statusBadge(viewing.status)}`}>
                  {viewing.status.toString().replace(/_/g, " ")}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>Full record view with print and conversion actions.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {viewing && Object.entries(viewing)
                .filter(([k]) => !["id", "_id", "created_by", "updated_by", "devices", "payments"].includes(k))
                // A TZS invoice has no rate or USD figures -- don't show empty boxes for them.
                .filter(([k, v]) => !(k in DETAIL_LABELS && (v === null || v === undefined)))
                .map(([k, v]) => (
                  <div key={k} className="border border-border rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{DETAIL_LABELS[k] || k.replace(/_/g, " ")}</p>
                    <p className="text-sm font-medium mt-1 break-words">{fmtCell(k, v)}</p>
                  </div>
                ))}
            </div>
            {cfg.devices && (viewing?.devices || []).filter((d) => d.serial_no).length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Devices Installed</p>
                <div className="border border-border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableHead className="text-xs">Device / Item</TableHead>
                        <TableHead className="text-xs">Serial Number</TableHead>
                        <TableHead className="text-xs">MAC Address</TableHead>
                        <TableHead className="text-xs">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewing.devices.filter((d) => d.serial_no).map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{d.device_name || "—"}</TableCell>
                          <TableCell className="text-sm font-mono">{d.serial_no}</TableCell>
                          <TableCell className="text-sm font-mono">{d.mac_address || "—"}</TableCell>
                          <TableCell className="text-sm">{d.notes || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {cfg.recordPayment && viewing && (
              <PaymentHistory
                key={viewing.id}
                invoice={viewing}
                canEdit={canEdit}
                onSaved={(updated) => { setViewing(updated); fetchData(); }}
              />
            )}
          </div>
          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
            {approvalTask?.can_act && (
              <div className="flex gap-2">
                <Button
                  onClick={() => { setApprovalNote(""); setApprovalAction("approve"); }}
                  data-testid="inline-approve-button"
                  className="bg-gradient-to-r from-emerald-500 to-green-600 text-white border-0 hover:opacity-95"
                >
                  <Check className="w-4 h-4 mr-2" /> Approve
                </Button>
                <Button
                  onClick={() => { setApprovalNote(""); setApprovalAction("reject"); }}
                  variant="outline"
                  data-testid="inline-reject-button"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                  <Ban className="w-4 h-4 mr-2" /> Reject
                </Button>
              </div>
            )}
            {canEdit && cfg.recordPayment && (() => {
              const isProforma = viewing?.doc_type === "Proforma Invoice";
              const isPaid = viewing?.status === "paid";
              const disabledReason = isProforma
                ? "Proforma invoices aren't a real bill yet — convert to a Tax Invoice before recording payment"
                : isPaid ? "This invoice is already fully paid" : "";
              return (
                <div className="flex flex-col gap-1">
                  <Button
                    onClick={() => { openPayment(viewing); setViewing(null); }}
                    disabled={isProforma || isPaid}
                    title={disabledReason}
                    data-testid="record-payment-button"
                    className="bg-gradient-to-r from-emerald-500 to-green-600 text-white border-0 hover:opacity-95 disabled:opacity-40"
                  >
                    <Wallet className="w-4 h-4 mr-2" /> Record Payment
                  </Button>
                  {disabledReason && (
                    <p className="text-[11px] text-muted-foreground max-w-[220px]" data-testid="record-payment-disabled-reason">
                      {disabledReason}
                    </p>
                  )}
                  {isProforma && (
                    <Button
                      variant="outline" size="sm"
                      onClick={convertProformaToTax}
                      disabled={convertingProforma}
                      data-testid="convert-to-tax-invoice-button"
                    >
                      <ArrowRight className="w-3.5 h-3.5 mr-1.5" />
                      {convertingProforma ? "Converting…" : "Convert to Tax Invoice"}
                    </Button>
                  )}
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button variant="outline" onClick={doPrint} data-testid="print-button">
                <Printer className="w-4 h-4 mr-2" /> Print / PDF
              </Button>
              {(cfg.convertTo || []).filter((t) => can(collectionOf(t.endpoint), "create")).map((t) => (
                <Button key={t.to} variant="outline" onClick={() => doConvert(t)} data-testid={`convert-${t.to.toLowerCase().replace(/\s/g, "-")}`}>
                  <ArrowRight className="w-4 h-4 mr-2" /> Convert to {t.to}
                </Button>
              ))}
              {cfg.workOrderPrefill && can("work_orders", "create") && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const prefill = cfg.workOrderPrefill(viewing);
                    setViewing(null);
                    navigate("/service/work-orders", { state: { prefillWorkOrder: prefill } });
                  }}
                  data-testid="create-work-order-button"
                >
                  <Wrench className="w-4 h-4 mr-2" /> Create Work Order
                </Button>
              )}
              {cfg.taskFromTicket && canViewPage("dwr") && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const t = { id: viewing.id, ticket_no: viewing.ticket_no, subject: viewing.subject, customer: viewing.customer };
                    setViewing(null);
                    navigate("/dwr/tasks", { state: { ticketForTask: t } });
                  }}
                  data-testid="create-task-from-ticket"
                >
                  <ListChecks className="w-4 h-4 mr-2" /> Create Task
                </Button>
              )}
              {canEdit && (
                <Button onClick={() => { openEdit(viewing); setViewing(null); }} className="kn-gradient-bg text-white border-0">
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment dialog -- posts the payment to the invoice AND books
          it as a normal Sale, per the "payments live in Sales" rule. */}
      <Dialog open={!!payingInvoice} onOpenChange={(o) => !o && setPayingInvoice(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Record Payment</DialogTitle>
            <DialogDescription>
              {payingInvoice && `Invoice ${payingInvoice.invoice_no} — outstanding TZS ${new Intl.NumberFormat("en-TZ").format(outstandingBalance(payingInvoice))}`}
              {payingInvoice?.currency === "USD" && payingInvoice.exchange_rate > 0 &&
                ` (≈ USD ${fmtCell("total_usd", outstandingBalance(payingInvoice) / payingInvoice.exchange_rate)} at ${fmtCell("exchange_rate", payingInvoice.exchange_rate)})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Amount Received</Label>
              <MoneyInput
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                data-testid="payment-amount-input"
                autoFocus
              />
              {payingInvoice?.currency === "USD" && payingInvoice.exchange_rate > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="payment-usd-equivalent">
                  Enter the TZS received. ≈ USD {fmtCell("total_usd", (Number(paymentAmount) || 0) / payingInvoice.exchange_rate)} at the invoice rate.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">
                Date Paid<span className="text-rose-500 ml-1">*</span>
              </Label>
              <Input
                type="date"
                value={paymentDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="payment-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">
                Payment Method<span className="text-rose-500 ml-1">*</span>
              </Label>
              <SearchableSelect
                value={paymentMethod}
                onValueChange={setPaymentMethod}
                options={paymentAccounts.map((a) => ({ value: a.name, label: a.name }))}
                placeholder="Select account…"
                testId="payment-method-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">Transaction ID (optional)</Label>
              <Input
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                data-testid="payment-transaction-id-input"
                placeholder="e.g. Mpesa/bank reference"
              />
            </div>
            <label className="flex items-start gap-2.5 rounded-lg border border-border p-3 cursor-pointer" data-testid="payment-historical">
              <Checkbox
                checked={paymentHistorical}
                onCheckedChange={(v) => setPaymentHistorical(v === true)}
                className="mt-0.5"
                data-testid="payment-historical-checkbox"
              />
              <span className="text-sm leading-snug">
                Old payment — already in account balance
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Records the dated installment and the sale, but doesn't add the money to {paymentMethod || "the account"}'s
                  current balance. Tick this when re-entering payments received in the past.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayingInvoice(null)}>Cancel</Button>
            <Button onClick={submitPayment} disabled={recordingPayment} data-testid="submit-payment-button" className="kn-gradient-bg text-white border-0">
              {recordingPayment ? "Recording…" : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inline approve/reject -- same confirm-with-note step as the Approvals page */}
      <Dialog open={!!approvalAction} onOpenChange={(o) => !o && setApprovalAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {approvalAction === "approve" ? "Approve this record?" : "Reject this record?"}
            </DialogTitle>
            <DialogDescription>
              {approvalAction === "approve"
                ? "This moves it to the next approval step, or marks it approved if this was the final step."
                : "This stops the request and notifies the requester."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label className="text-xs uppercase tracking-[0.16em]">Note (optional)</Label>
            <Textarea value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} rows={3} data-testid="approval-note-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApprovalAction(null)}>Cancel</Button>
            <Button
              onClick={submitApproval}
              disabled={submittingApproval}
              data-testid="confirm-approval-action"
              className={approvalAction === "approve"
                ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white border-0"
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}
            >
              {submittingApproval ? "Submitting…" : approvalAction === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
